import React, { useState, useEffect } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Link, useNavigate, useParams } from "react-router-dom";
import { 
  Card, 
  Row, 
  Col, 
  Form, 
  Button, 
  Alert, 
  Spinner, 
 
  InputGroup,
  Modal
} from "react-bootstrap";
import {
  Save,
  ArrowLeft,
  CheckCircle,
  ExclamationCircle,
 
  InfoCircle,
  PlayCircle,
  PauseCircle,
  FileText,
  Tag,
  Percent as PercentIcon,
  Pencil,

} from "react-bootstrap-icons";

// Données de démo pour les taxes
const demoTaxes = [
  {
    id: 1,
    code: "TF001",
    nom: "Taxe Foncière Urbaine",
    description: "Taxe applicable aux propriétés urbaines",
    type: "fonciere",
    taux: 15.5,
    devise: "XOF",
    periodicite: "annuelle",
    statut: "active",
    dateDebut: "2023-01-01",
    dateFin: "2023-12-31",
    categorie: "generale"
  },
  {
    id: 2,
    code: "TP001",
    nom: "Taxe Professionnelle",
    description: "Taxe sur les activités professionnelles",
    type: "professionnelle",
    taux: 18.5,
    devise: "XOF",
    periodicite: "annuelle",
    statut: "active",
    dateDebut: "2023-01-01",
    dateFin: "2023-12-31",
    categorie: "specifique"
  }
];

export default function EditTaxe() {
  const navigate = useNavigate();
  const { id } = useParams();
  
  // États du formulaire
  const [formData, setFormData] = useState({
    code: "",
    nom: "",
    description: "",
    type: "fonciere",
    taux: "",
    devise: "XOF",
    periodicite: "annuelle",
    statut: "active",
    dateDebut: "",
    dateFin: "",
    categorie: "generale"
  });

  // États pour la gestion
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Types de taxes
  const taxTypes = [
    { value: "fonciere", label: "Taxe foncière", color: "primary" },
    { value: "professionnelle", label: "Taxe professionnelle", color: "success" },
    { value: "habitation", label: "Taxe d'habitation", color: "info" },
    { value: "vehicule", label: "Taxe sur les véhicules", color: "warning" },
    { value: "publicite", label: "Taxe de publicité", color: "dark" }
  ];

  // Périodicités
  const periodicites = [
    { value: "annuelle", label: "Annuelle" },
    { value: "semestrielle", label: "Semestrielle" },
    { value: "trimestrielle", label: "Trimestrielle" },
    { value: "mensuelle", label: "Mensuelle" },
    { value: "ponctuelle", label: "Ponctuelle" }
  ];

  // Catégories
  const categories = [
    { value: "generale", label: "Générale" },
    { value: "specifique", label: "Spécifique" },
    { value: "exceptionnelle", label: "Exceptionnelle" },
    { value: "locale", label: "Locale" },
    { value: "nationale", label: "Nationale" }
  ];

  // Devises
  const devises = [
    { value: "XOF", label: "Franc CFA (XOF)" },
    { value: "EUR", label: "Euro (EUR)" },
    { value: "USD", label: "Dollar (USD)" },
    { value: "XAF", label: "Franc CFA BEAC (XAF)" }
  ];

  // Charger la taxe à modifier
  useEffect(() => {
    const loadTax = () => {
      setLoading(true);
      setTimeout(() => {
        const tax = demoTaxes.find(t => t.id === parseInt(id));
        if (tax) {
          setFormData({
            code: tax.code,
            nom: tax.nom,
            description: tax.description || "",
            type: tax.type,
            taux: tax.taux.toString(),
            devise: tax.devise,
            periodicite: tax.periodicite,
            statut: tax.statut,
            dateDebut: tax.dateDebut,
            dateFin: tax.dateFin,
            categorie: tax.categorie || "generale"
          });
        } else {
          showAlert("Taxe non trouvée", "danger");
          navigate("/superAdmin/Taxes");
        }
        setLoading(false);
      }, 800);
    };

    if (id) {
      loadTax();
    }
  }, [id, navigate]);

  // Gestion des alertes
  const [alert, setAlert] = useState({ show: false, message: "", type: "info" });

  const showAlert = (message, type = "info", duration = 5000) => {
    setAlert({ show: true, message, type });
    if (duration > 0) {
      setTimeout(() => {
        setAlert({ show: false, message: "", type: "info" });
      }, duration);
    }
  };

  // Validation simplifiée
  const validateField = (field, value) => {
    let error = "";
    
    switch(field) {
      case 'code':
        if (!value.trim()) error = "Le code de la taxe est requis";
        else if (!/^[A-Z0-9]{2,10}$/.test(value)) error = "2 à 10 caractères majuscules/chiffres";
        break;
      case 'nom':
        if (!value.trim()) error = "Le nom de la taxe est requis";
        else if (value.length < 3) error = "Minimum 3 caractères";
        break;
      case 'taux':
        if (!value) error = "Le taux est requis";
        else if (value !== "variable") {
          const num = parseFloat(value);
          if (isNaN(num)) error = "Doit être un nombre";
          else if (num < 0 || num > 100) error = "Doit être entre 0 et 100";
        }
        break;
      case 'dateFin':
        if (!value) error = "La date de fin est requise";
        else if (new Date(value) < new Date(formData.dateDebut)) {
          error = "Doit être après la date de début";
        }
        break;
      default:
        break;
    }
    
    setErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  // Validation du formulaire
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    ['code', 'nom', 'type', 'taux', 'devise', 'periodicite', 'statut', 'dateDebut', 'dateFin', 'categorie'].forEach(key => {
      if (!formData[key] || formData[key].toString().trim() === '') {
        newErrors[key] = "Ce champ est requis";
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    return isValid;
  };

  // Gestion des changements
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched[field]) validateField(field, value);
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showAlert("Veuillez corriger les erreurs dans le formulaire", "danger");
      return;
    }

    setSaving(true);
    
    // Simulation de sauvegarde
    setTimeout(() => {
      setSaving(false);
      setShowSuccessModal(true);
    }, 1200);
  };

  // Formatage des dates
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <div className="container-fluid">
            <Row>
              <Col md={12}>
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" className="me-3" />
                  <span className="text-muted">Chargement de la taxe...</span>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header />
      
      <div className="main main-app p-3 p-lg-4">
        <div className="container-fluid">
          {/* Alerte */}
          {alert.show && (
            <Alert 
              variant={alert.type} 
              dismissible 
              onClose={() => setAlert({ show: false, message: "", type: "info" })}
              className="shadow-sm mb-4"
            >
              <div className="d-flex align-items-center">
                {alert.type === 'success' ? <CheckCircle className="me-2 fs-5" /> :
                 alert.type === 'danger' ? <ExclamationCircle className="me-2 fs-5" /> :
                 <InfoCircle className="me-2 fs-5" />}
                <span className="fw-medium">{alert.message}</span>
              </div>
            </Alert>
          )}

          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item"><Link to="/superAdmin/dashboard">Tableau de bord</Link></li>
                    <li className="breadcrumb-item"><Link to="/superAdmin/Taxes">Taxes</Link></li>
                    <li className="breadcrumb-item active" aria-current="page">Modifier la taxe</li>
                  </ol>
                  <h4 className="main-title mb-0">
                    <Pencil className="me-2" />
                    Modifier la taxe
                  </h4>
                  <p className="text-muted mb-0">Modifiez les informations de la taxe {formData.code}</p>
                </div>

                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-secondary" 
                    as={Link} 
                    to="/superAdmin/Taxes"
                    className="d-flex align-items-center"
                  >
                    <ArrowLeft className="me-2" size={16} />
                    Retour
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-primary text-white py-3">
                  <div className="d-flex align-items-center">
                    <Pencil className="me-2" />
                    <span className="fw-semibold">Informations de la taxe</span>
                  </div>
                </Card.Header>
                
                <Card.Body>
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Code de la taxe <span className="text-danger">*</span>
                          </Form.Label>
                          <InputGroup hasValidation>
                            <InputGroup.Text>
                              <Tag />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Ex: TF001"
                              value={formData.code}
                              onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
                              onBlur={() => handleBlur("code")}
                              isInvalid={!!errors.code && touched.code}
                              maxLength={10}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.code}
                            </Form.Control.Feedback>
                          </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>
                            Nom de la taxe <span className="text-danger">*</span>
                          </Form.Label>
                          <InputGroup hasValidation>
                            <InputGroup.Text>
                              <FileText />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Ex: Taxe Foncière Urbaine"
                              value={formData.nom}
                              onChange={(e) => handleChange("nom", e.target.value)}
                              onBlur={() => handleBlur("nom")}
                              isInvalid={!!errors.nom && touched.nom}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.nom}
                            </Form.Control.Feedback>
                          </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Description détaillée de la taxe..."
                            value={formData.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                            maxLength={500}
                          />
                          <Form.Text className="text-muted">
                            {formData.description.length}/500 caractères
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>
                            Type de taxe <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            value={formData.type}
                            onChange={(e) => handleChange("type", e.target.value)}
                            isInvalid={!!errors.type}
                          >
                            <option value="">Sélectionnez un type</option>
                            {taxTypes.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </Form.Select>
                          {errors.type && (
                            <Form.Control.Feedback type="invalid">
                              {errors.type}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Taux <span className="text-danger">*</span>
                          </Form.Label>
                          <InputGroup hasValidation>
                            <InputGroup.Text>
                              <PercentIcon />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Ex: 15.5 ou 'variable'"
                              value={formData.taux}
                              onChange={(e) => handleChange("taux", e.target.value)}
                              onBlur={() => handleBlur("taux")}
                              isInvalid={!!errors.taux && touched.taux}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.taux}
                            </Form.Control.Feedback>
                          </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>
                            Devise <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            value={formData.devise}
                            onChange={(e) => handleChange("devise", e.target.value)}
                            isInvalid={!!errors.devise}
                          >
                            {devises.map(devise => (
                              <option key={devise.value} value={devise.value}>
                                {devise.label}
                              </option>
                            ))}
                          </Form.Select>
                          {errors.devise && (
                            <Form.Control.Feedback type="invalid">
                              {errors.devise}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>
                            Périodicité <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            value={formData.periodicite}
                            onChange={(e) => handleChange("periodicite", e.target.value)}
                            isInvalid={!!errors.periodicite}
                          >
                            {periodicites.map(period => (
                              <option key={period.value} value={period.value}>
                                {period.label}
                              </option>
                            ))}
                          </Form.Select>
                          {errors.periodicite && (
                            <Form.Control.Feedback type="invalid">
                              {errors.periodicite}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Date de début <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="date"
                                value={formData.dateDebut}
                                onChange={(e) => handleChange("dateDebut", e.target.value)}
                                isInvalid={!!errors.dateDebut}
                              />
                              {errors.dateDebut && (
                                <Form.Control.Feedback type="invalid">
                                  {errors.dateDebut}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Date de fin <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="date"
                                value={formData.dateFin}
                                onChange={(e) => handleChange("dateFin", e.target.value)}
                                onBlur={() => handleBlur("dateFin")}
                                isInvalid={!!errors.dateFin && touched.dateFin}
                              />
                              {errors.dateFin && (
                                <Form.Control.Feedback type="invalid">
                                  {errors.dateFin}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>

                        <Form.Group className="mb-3">
                          <Form.Label>
                            Catégorie <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            value={formData.categorie}
                            onChange={(e) => handleChange("categorie", e.target.value)}
                            isInvalid={!!errors.categorie}
                          >
                            {categories.map(cat => (
                              <option key={cat.value} value={cat.value}>
                                {cat.label}
                              </option>
                            ))}
                          </Form.Select>
                          {errors.categorie && (
                            <Form.Control.Feedback type="invalid">
                              {errors.categorie}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>
                            Statut <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="d-flex gap-3">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="statut-active"
                                checked={formData.statut === "active"}
                                onChange={() => handleChange("statut", "active")}
                              />
                              <label className="form-check-label d-flex align-items-center" htmlFor="statut-active">
                                <PlayCircle className="text-success me-2" />
                                Active
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="statut-inactive"
                                checked={formData.statut === "inactive"}
                                onChange={() => handleChange("statut", "inactive")}
                              />
                              <label className="form-check-label d-flex align-items-center" htmlFor="statut-inactive">
                                <PauseCircle className="text-secondary me-2" />
                                Inactive
                              </label>
                            </div>
                          </div>
                          {errors.statut && (
                            <div className="text-danger small mt-1">{errors.statut}</div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Boutons d'action */}
                    <div className="border-top pt-4 mt-4">
                      <div className="d-flex justify-content-between">
                        <Button
                          variant="outline-secondary"
                          as={Link}
                          to="/superAdmin/Taxes"
                          className="d-flex align-items-center"
                        >
                          <ArrowLeft className="me-2" />
                          Annuler
                        </Button>

                        <Button
                          variant="primary"
                          type="submit"
                          disabled={saving}
                          className="d-flex align-items-center"
                        >
                          {saving ? (
                            <>
                              <Spinner size="sm" animation="border" className="me-2" />
                              Sauvegarde...
                            </>
                          ) : (
                            <>
                              <Save className="me-2" />
                              Enregistrer les modifications
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* Modal de succès */}
        <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="text-success">
              <CheckCircle className="me-2" />
              Taxe mise à jour avec succès
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <CheckCircle className="display-1 text-success mb-3" />
            <h5>Les modifications ont été enregistrées</h5>
            <p className="text-muted">
              La taxe <strong>{formData.nom}</strong> a été mise à jour.
            </p>
          </Modal.Body>
          <Modal.Footer className="border-0 justify-content-center">
            <Button variant="secondary" onClick={() => setShowSuccessModal(false)} className="me-3">
              Continuer l'édition
            </Button>
            <Button variant="primary" onClick={() => navigate("/superAdmin/Taxes")}>
              Retour à la liste
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}